// web/src/components/AsyncBoundary.tsx — the empty-screen killer (D05).
// loading → skeleton; error → message + Retry(refetch); empty → EmptyState (one CTA); else children(data).
// NEVER renders null/blank — the 3-state contract is unskippable.
import type { ReactNode } from 'react';
import { EmptyState } from './EmptyState';
import { Icon } from './Icon';

export function AsyncBoundary<T>({ query, skeleton, isEmpty, empty, children }: {
  query: { data?: T; isLoading: boolean; error?: unknown; refetch(): void };
  skeleton: ReactNode;
  isEmpty?: (d: T) => boolean;
  empty?: { icon: string; title: string; body?: string; cta?: { label: string; onClick(): void } };
  children: (d: T) => ReactNode;
}) {
  if (query.isLoading) return <>{skeleton}</>;
  if (query.error) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-danger/40 bg-surface/40 px-6 py-10 text-center">
        <span className="flex size-12 items-center justify-center rounded-full bg-surface2 text-danger"><Icon name="alert" size={24} /></span>
        <p className="text-sm text-muted">Something went wrong loading this.</p>
        <button onClick={() => query.refetch()}
          className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink transition-transform active:scale-[0.98]">
          <Icon name="refresh" size={16} />Retry
        </button>
      </div>
    );
  }
  const data = query.data as T;
  if (data !== undefined && isEmpty?.(data) && empty) return <EmptyState {...empty} />;
  return <>{children(data)}</>;
}
